import { createWebHistory, createRouter } from 'vue-router';
import adminRoutes from './AdminRoutes';
import authRoutes from './authRoutes';
import { getItem } from '../utility/localStorageControl';
import { checkUserRoleAccess } from '@/helpers/global-helper'
import { meta } from "./meta";
import { setMeta } from './meta';

// Define the base URL dynamically
const baseHistory = createWebHistory(process.env.BASE_URL || '/'); // Use BASE_URL or a fallback

const routes = [
  {
    name: 'Admin',
    path: '/',
    component: () => import(/* webpackChunkName: "admin" */ '@/layout/withAdminLayout.vue'),
    children: [...adminRoutes],
    meta: {
      ...meta['admin']
    }
  },
  {
    name: 'Auth',
    path: '/auth',
    component: () => import(/* webpackChunkName: "auth" */ '@/layout/withAuthLayout.vue'),
    children: [...authRoutes],
    meta: {
      ...meta['login']
    }
  },
  {
    path: '/:catchAll(.*)', // Catch-all route to handle 404
    component: () => import(/* webpackChunkName: "auth" */ '@/layout/withAuthLayout.vue'),
    children: [
      {
        path: '',
        name: 'NotFound',
        component: () => import(/* webpackChunkName: "Blank" */ '@/view/ErrorPage/404.vue')
      }
    ],
    meta: {
      ...meta['error-404']
    }
  },
  {
    path: '/error-401', // Catch-all route to handle 404
    component: () => import(/* webpackChunkName: "auth" */ '@/layout/withAuthLayout.vue'),
    children: [
      {
        path: '/error-401',
        name: 'PagePermission',
        component: () => import(/* webpackChunkName: "Blank" */ '@/view/ErrorPage/401.vue')
      }
    ],
    meta: {
      ...meta['error-401']
    }
  },
  {
    path: '/expired-400', // Catch-all route to handle 404
    component: () => import(/* webpackChunkName: "auth" */ '@/layout/withAuthLayout.vue'),
    children: [
      {
        path: '/expired-400',
        name: 'expired',
        component: () => import(/* webpackChunkName: "Blank" */ '@/view/ErrorPage/400.vue')
      }
    ],
    meta: {
      ...meta['error-400']
    }
  },
];

// // Redirect pages
const isAuth = [
  "login",
  // "register",
  "forgotPassword",
  "update-password",
  "expired"
]


// Add names of routes that require login
const isLogin = [
  // Error Page
  {name: 'NotFound',                    module: 'error'},
  {name: 'PagePermission',              module: 'error'},
  // Default Page
  {name: 'dashboard',                   module: 'dashboard'},
  {name: 'profile',                     module: 'View Profile'},
  // Page with permission
  {name: 'admin-user-management',       module: 'View Users'},
  {name: 'admin-user-management-view',  module: 'View Users'},
  {name: 'admin-user-management-create',  module: 'Add User'},
  {name: 'admin-user-management-update',  module: 'Edit User'},
  {name: 'role-management',             module: 'View Roles'},
  {name: 'role-management-create',      module: 'Add Role'},
  {name: 'role-management-update',      module: 'Edit Role'},
  {name: 'barangay-health-station',     module: 'View BHS'},
  {name: 'barangay-health-station-view-one', module: 'View One BHS'},
  // {name: 'add-bhs-facility',            module: 'View All BHS'},
  {name: 'vaccines',                    module: 'View Vaccines'},
  {name: 'vaccines-view-one',           module: 'View One Vaccines'},
  {name: 'antigens',                    module: 'View Antigens'},
  {name: 'antigen-view-one',            module: 'View One Antigens'},
  {name: 'NotFound',                    module: 'error'},
  {name: 'PagePermission',              module: 'error'},
  {name: 'psgc-region',                 module: 'View Regions'},
  {name: 'psgc-region-view',            module: 'View Regions'},
  {name: 'psgc-province',               module: 'View Provinces'},
  {name: 'psgc-province-view',          module: 'View Provinces'},
  {name: 'psgc-city',                   module: 'View Cities'},
  {name: 'psgc-city-view',              module: 'View Cities'},
  {name: 'psgc-barangay',               module: 'View Barangays'},
  {name: 'tcl',                         module: 'View All TCL'},
  {name: 'tcl-life-stage',              module: 'Add TCL'},
  {name: 'tcl-life-stage-antigen-nutrition', module: 'Add TCL'},
  {name: 'tcl-life-stage-antigen-doze', module: 'Add TCL'},
  {name: 'tcl-life-stage-nutrition-doze', module: 'Add TCL'},
  {name: 'rural-health-unit',           module: 'View All RHU'},
  {name: 'rural-health-unit-view-one',  module: 'View One RHU'},
  {name: 'city-health-office',          module: ''},
  {name: 'health-facilities',           module: 'View All Organizations'},
  {name: 'create-health-facilities',    module: 'Add Organization'},
  {name: 'update-health-facilities',    module: 'Edit Organization'},
  {name: 'view-one-health-facilities',  module: 'View One Organization'},
  {name: 'facility-user',               module: 'View One Organization'},   
  {name: 'facility-user-update',        module: 'View One Organization'},    
  {name: 'facility-user-create',        module: 'View One Organization'},  
  {name: 'target-management',           module: 'View All Targets'},
]; 

const router = createRouter({
  history: baseHistory,
  routes: routes,
});

router.beforeEach((to, from, next) => {
  document.title = meta[to.name] ? meta[to.name].title : ''; //Set page Meta

  const authToken = getItem('access_token');
  const userProfile = getItem('user_profile')
  const listRoleName = userProfile ? userProfile.roles?.flatMap(role => role.name) : []

  const isAuthenticated = authToken ? true : false;

  const routeRequiresAuth = isLogin.some(route => route.name === to.name);
  const defaultModules = ['error', 'dashboard', 'profile']

  setMeta(to, from);
  if (isAuthenticated) {
    if (isAuth.includes(to.name)) {
      next('/'); // Redirect to home if already logged in
    } else {
      const matchedRoute = findRouteModule(to.name);
      if(checkUserRoleAccess(matchedRoute.module) || defaultModules.includes(matchedRoute.module) ||  listRoleName.includes('Super Admin')) {
        next();
      } else {
        next('/error-401');
      }
    }
  } else if (!isAuthenticated) {
    if (routeRequiresAuth) {
      next('/auth/login'); 
    } else {
      next();
    }
  }
});

const findRouteModule = (routeName) => {
  // Use Array.prototype.find() to search for the route by name
  const route = isLogin.find((item) => item.name === routeName);

  if (route) {
    // If a match is found, return the name and module
    return { name: route.name, module: route.module };
  } else {
    // If no match is found, return undefined or a default value
    return undefined;
  }
};

export default router;
